// ═══════════════════════════════════════════════════════════════
// ClearView CRM Member Service — Supabase CRUD
// ═══════════════════════════════════════════════════════════════
import { supabase } from "../lib/supabase";
import { toSnakeCase, toCamelCase } from "../utils/fieldMapper";
import { debugLog } from "../utils/debugLog";

export const memberService = {
  /**
   * Fetch all members with their contact logs
   */
  async getAll() {
    // Fetch members WITHOUT contact_logs for performance
    // Contact logs are loaded lazily when opening a donor's detail view
    const pageSize = 1000;
    let allData = [];
    let lastId = null;

    while (true) {
      let query = supabase
        .from("members")
        .select("*")
        .order("id", { ascending: true })
        .limit(pageSize);

      if (lastId) query = query.gt("id", lastId);

      const { data, error } = await query;

      if (error) {
        debugLog.error("memberService.getAll", error);
        throw error;
      }

      if (!data || data.length === 0) break;
      allData = allData.concat(data);
      lastId = data[data.length - 1].id;
      if (data.length < pageSize) break;
    }

    debugLog.data("Members loaded from Supabase", { count: allData.length });
    return allData.map(toCamelCase);
  },

  /**
   * Create a new member
   */
  async create(memberData) {
    const dbData = toSnakeCase(memberData);
    // Remove fields that shouldn't be inserted
    delete dbData.id;
    delete dbData.contact_log;

    const { data, error } = await supabase
      .from("members")
      .insert(dbData)
      .select("*, contact_logs(*)")
      .single();

    if (error) {
      debugLog.error("memberService.create", error);
      throw error;
    }
    debugLog.action("Member created in Supabase", { id: data.id });
    return toCamelCase(data);
  },

  /**
   * Update an existing member
   */
  async update(id, updates) {
    const dbUpdates = toSnakeCase(updates);
    // Remove fields that shouldn't be in an update
    delete dbUpdates.id;
    delete dbUpdates.contact_log;
    delete dbUpdates.contact_logs;
    delete dbUpdates.created_at;

    const { data, error } = await supabase
      .from("members")
      .update(dbUpdates)
      .eq("id", id)
      .select("*, contact_logs(*)")
      .single();

    if (error) {
      debugLog.error("memberService.update", error);
      throw error;
    }
    debugLog.action("Member updated in Supabase", { id });
    return toCamelCase(data);
  },

  /**
   * Delete a member (cascade deletes contact logs)
   */
  async remove(id) {
    const { error } = await supabase.from("members").delete().eq("id", id);

    if (error) {
      debugLog.error("memberService.remove", error);
      throw error;
    }
    debugLog.action("Member deleted from Supabase", { id });
  },

  /**
   * Import multiple members (bulk insert)
   * Returns { added: number, updated: number }
   */
  async importBulk(members) {
    const toAdd = [];
    const toUpdate = [];

    // Check for existing AFP IDs
    const afpIds = members.filter((m) => m.afpId).map((m) => m.afpId);
    let existingAfpIds = new Set();

    if (afpIds.length > 0) {
      const { data: existing } = await supabase
        .from("members")
        .select("afp_id")
        .in("afp_id", afpIds);
      existingAfpIds = new Set((existing || []).map((r) => r.afp_id));
    }

    members.forEach((member) => {
      const dbData = toSnakeCase(member);
      delete dbData.id;
      delete dbData.contact_log;

      if (member.afpId && existingAfpIds.has(member.afpId)) {
        toUpdate.push(dbData);
      } else {
        toAdd.push(dbData);
      }
    });

    // Insert new members
    if (toAdd.length > 0) {
      const { error } = await supabase.from("members").insert(toAdd);
      if (error) {
        debugLog.error("memberService.importBulk insert", error);
        throw error;
      }
    }

    // Update existing members by AFP ID
    for (const member of toUpdate) {
      const afpId = member.afp_id;
      delete member.afp_id; // Don't update the key field
      // Don't overwrite existing data with blanks from CSV
      Object.keys(member).forEach((k) => {
        if (member[k] === null || member[k] === undefined || member[k] === "") {
          delete member[k];
        }
      });

      const { error } = await supabase
        .from("members")
        .update(member)
        .eq("afp_id", afpId);
      if (error) {
        debugLog.error("memberService.importBulk update", error);
      }
    }

    debugLog.data("Import complete", {
      added: toAdd.length,
      updated: toUpdate.length,
    });
    return { added: toAdd.length, updated: toUpdate.length };
  },
};
